import { Link } from "react-router-dom";
import {
  ArrowRight, ShieldCheck, Users, Phone, Building2, Heart,
  Presentation, GraduationCap, Sparkles, BarChart3, Check, HeartHandshake,
} from "lucide-react";
import usePageMeta from "../../lib/usePageMeta";
import AccessModelCallout from "../../components/public/AccessModelCallout";

const CORE_TIERS = [
  {
    id: "remote-virtual",
    label: "Remote Care",
    icon: Phone,
    bullets: [
      "Confidential support through configured phone, text, message, and available remote-support channels.",
      "Access to male, female, and bilingual chaplains where available within the organization's care arrangement.",
      "Dedicated partner webpage and employee literature.",
      "Monthly encouragement check-ins for employees who opt in.",
      "Support for employees and their immediate household members.",
    ],
  },
  {
    id: "onsite",
    label: "On-Site Chaplain Presence",
    icon: Users,
    bullets: [
      "Regular scheduled workplace visits.",
      "Relationship-based care designed to build familiarity and trust.",
      "One-on-one employee and family support within appropriate chaplain scope.",
      "Participation in approved company meetings, events, and employee-support initiatives.",
      "Coordination with organizational leadership regarding the structure of on-site chaplain presence.",
    ],
  },
  {
    id: "crisis",
    label: "Enhanced Crisis & Organizational Response",
    icon: Heart,
    bullets: [
      "Hospital visits and family support.",
      "Funeral and memorial assistance.",
      "Workplace crisis and critical-incident support.",
      "Leadership consultation during significant employee or organizational events.",
      "Coordinated on-site or remote crisis response based on the organization's service arrangement, location, circumstances, and chaplain availability.",
    ],
  },
];

const INCLUDED = [
  {
    id: "employee-access",
    label: "24/7 Access & Digital Support",
    icon: HeartHandshake,
    bullets: [
      "24/7 on-call access to a CCCHAPS chaplain.",
      "CALLACHAP access for employees.",
      "CCCHAPS Care Portal access.",
      "CCCHAPS On Demand access.",
      "Confidential support for employees and their immediate household members.",
      "Phone, text, confidential message, and other configured remote-support channels based on the organization's service arrangement.",
    ],
  },
  {
    id: "reporting",
    label: "Aggregate Reporting & Organizational Awareness",
    icon: BarChart3,
    bullets: [
      "Monthly utilization and interaction reporting.",
      "Aggregate, non-identifying summaries of areas of support.",
      "Non-identifying patterns that may help organizational leaders understand where employees are seeking support.",
      "Aggregate utilization and service-engagement indicators.",
    ],
  },
  {
    id: "leadership",
    label: "Leadership & Organizational Support",
    icon: Building2,
    bullets: [
      "Leadership consultation and confidential support.",
      "Guidance for leaders navigating sensitive employee-support situations while respecting appropriate organizational boundaries.",
      "Workshops and webinars.",
      "Keynote and special presentations.",
      "Customized organizational services.",
    ],
  },
  {
    id: "partner",
    label: "Partner Resources",
    icon: GraduationCap,
    bullets: [
      "Educational and workplace resources.",
      "Employee-support content.",
      "Customized training and workshops.",
      "Collaboration with organizational leadership regarding appropriate support resources.",
      "Additional workplace-support materials based on the organization's care arrangement.",
    ],
  },
];

const ADD_ONS = [
  { icon: Presentation, label: "Lunch-and-learn presentations" },
  { icon: GraduationCap, label: "Employee well-being education" },
  { icon: Building2, label: "Leadership development and support training" },
  { icon: Users, label: "New-employee orientation or outreach" },
  { icon: Sparkles, label: "Customized initiatives developed around organizational needs" },
];

export default function Packages() {
  usePageMeta({
    title: "Care Team Packages | California Corporate Chaplains",
    description:
      "CCCHAPS Care Team Packages are built around your organization — combining remote, on-site, and enhanced crisis chaplaincy with 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand.",
  });

  return (
    <div className="bg-white" data-testid="packages-page">
      {/* HERO */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-7">
            <p className="text-xs uppercase tracking-[0.22em] text-[#4A6FA5] font-semibold mb-3">For Organizations</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl tracking-tight text-slate-900 leading-[1.05]">
              Care Team Packages
            </h1>
            <p className="mt-6 text-xl text-slate-700 leading-relaxed">
              We specialize in building workplace chaplain care arrangements around the needs of each organization.
            </p>
            <p className="mt-6 text-slate-600 leading-relaxed">
              Every organization is different. California Corporate Chaplains works with employers, healthcare organizations, hospices, senior living communities, board and care facilities, and other organizations to combine services based on workforce size, organizational needs, location, operating environment, and the level of support you want to offer your people.
            </p>

            {/* 24/7 baseline — universal, not tier-gated */}
            <div
              data-testid="packages-247-baseline"
              className="mt-8 rounded-xl border border-[#4A6FA5]/25 bg-[#F0F4F8] p-5 flex items-start gap-3"
            >
              <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
              <div className="text-sm text-slate-700 leading-relaxed">
                <p>
                  <span className="font-semibold text-slate-900">24/7 on-call chaplain access is standard with every CCCHAPS service arrangement</span> — from full workplace chaplaincy programs to on-call-only CALLACHAP arrangements, and regardless of month-to-month or annual terms.
                </p>
                <p className="mt-1.5 text-xs text-slate-600">
                  CALLACHAP is included with your CCCHAPS chaplaincy service — employees do not pay to use it.
                </p>
              </div>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div
              className="aspect-[4/5] rounded-2xl overflow-hidden shadow-sm border border-slate-200 bg-slate-100"
              data-testid="packages-hero-image"
            >
              <img
                src="/images/packages/hero.jpg"
                alt="A small group of workplace professionals in a genuine, collaborative conversation."
                loading="eager"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>

        <AccessModelCallout
          variant="default"
          className="mt-12"
          testId="packages-access-model-callout"
        />
      </section>

      {/* BUILD YOUR PACKAGE */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
        <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Step 1</p>
        <h2 className="mt-1 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Build Your Care Team Package</h2>
        <p className="mt-3 text-slate-600 max-w-3xl leading-relaxed">
          These are not preset package levels. Your care arrangement is built around your organization rather than forced into a Bronze/Silver/Gold-style package. Organizations can combine one or more care components based on workforce needs, geography, operating environment, and the level of chaplain presence desired.
        </p>
        <p className="mt-3 text-sm text-slate-600 leading-relaxed max-w-3xl" data-testid="step1-baseline-note">
          Every Care Team Package includes 24/7 on-call chaplain access, CALLACHAP, the CCCHAPS Care Portal, and CCCHAPS On Demand. The care components below determine how additional chaplain presence and organizational support are structured.
        </p>
        <div className="mt-10 grid md:grid-cols-3 gap-6">
          {CORE_TIERS.map((t) => (
            <article key={t.id} data-testid={`tier-${t.id}`} className="rounded-2xl border border-slate-200 bg-white p-6 hover:border-[#4A6FA5] hover:shadow-md transition">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]"><t.icon className="w-5 h-5" strokeWidth={1.75} /></span>
              <h3 className="mt-4 font-heading text-lg text-slate-900">{t.label}</h3>
              <ul className="mt-3 space-y-2 text-sm text-slate-700 leading-relaxed">
                {t.bullets.map((b, i) => (
                  <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" /> <span>{b}</span></li>
                ))}
              </ul>
            </article>
          ))}
        </div>
        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-4 flex items-start gap-3" data-testid="crisis-baseline-clarification">
          <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
          <p className="text-sm text-slate-700 leading-relaxed">
            <span className="font-semibold text-slate-900">Every CCCHAPS service arrangement includes 24/7 access to an on-call chaplain.</span>{" "}
            Enhanced Crisis &amp; Organizational Response adds a broader coordinated support capability for significant workplace, employee, or family events when included in the organization's care arrangement.
          </p>
        </div>
      </section>

      {/* EVERY PACKAGE INCLUDES */}
      <section className="bg-[#F8FAFC] border-y border-slate-200">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Step 2</p>
          <h2 className="mt-1 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Every Package Includes</h2>

          {/* Privacy call-out */}
          <div className="mt-6 rounded-xl border border-slate-200 bg-white p-5 flex items-start gap-3" data-testid="privacy-callout">
            <ShieldCheck className="w-5 h-5 text-[#4A6FA5] shrink-0 mt-0.5" strokeWidth={1.75} />
            <p className="text-sm text-slate-700 leading-relaxed">
              <strong className="font-semibold text-slate-900">Organizational reporting is aggregate and non-identifying.</strong> California Corporate Chaplains does not provide employers with the private details of individual confidential chaplain conversations.
            </p>
          </div>

          <p
            className="mt-4 font-heading text-lg sm:text-xl text-slate-900 leading-snug italic"
            data-testid="packages-org-impact-line"
          >
            The organization sees the impact. The employee keeps the conversation.
          </p>

          <div className="mt-8 grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {INCLUDED.map((t) => (
              <article key={t.id} data-testid={`included-${t.id}`} className="rounded-2xl border border-slate-200 bg-white p-6">
                <span className="inline-flex items-center justify-center w-10 h-10 rounded-lg bg-[#F0F4F8] text-[#4A6FA5]"><t.icon className="w-5 h-5" strokeWidth={1.75} /></span>
                <h3 className="mt-4 font-heading text-lg text-slate-900">{t.label}</h3>
                <ul className="mt-3 space-y-2 text-sm text-slate-700 leading-relaxed">
                  {t.bullets.map((b, i) => (
                    <li key={i} className="flex gap-2"><Check className="w-4 h-4 text-[#4A6FA5] shrink-0 mt-0.5" /> <span>{b}</span></li>
                  ))}
                </ul>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* OPTIONAL ADD-ONS */}
      <section className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <p className="text-xs uppercase tracking-[0.16em] text-slate-500 font-semibold">Step 3</p>
        <h2 className="mt-1 font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Optional Add-Ons</h2>
        <p className="mt-3 text-slate-600 max-w-2xl">Layer additional services into your care arrangement based on the needs and moments that matter most to your workforce.</p>
        <ul className="mt-8 grid sm:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="add-ons">
          {ADD_ONS.map((a, i) => (
            <li key={i} className="rounded-xl border border-slate-200 bg-white p-4 flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-8 h-8 rounded-md bg-[#F0F4F8] text-[#4A6FA5]"><a.icon className="w-4 h-4" strokeWidth={1.75} /></span>
              <span className="text-sm text-slate-800">{a.label}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* CTA */}
      <section className="border-t border-slate-200">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20 text-center">
          <h2 className="font-heading text-3xl sm:text-4xl tracking-tight text-slate-900">Every Care Team Package is customized to your organization.</h2>
          <p className="mt-4 text-slate-600 leading-relaxed max-w-2xl mx-auto">
            Pricing depends on workforce size, service mix, territory, level of chaplain presence, and the scope of organizational support requested. We can discuss your workforce, location, priorities, and preferred care model and build a proposal around the level of support that fits your organization.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Link
              to="/schedule?type=organizational_consultation"
              data-testid="packages-cta-request"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg bg-[#4A6FA5] text-white font-medium hover:bg-[#3B597B]"
            >
              Request a Custom Care Team Package <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/contact"
              data-testid="packages-cta-contact"
              className="inline-flex items-center gap-2 px-6 py-3.5 rounded-lg border border-slate-300 text-slate-800 font-medium hover:border-[#4A6FA5] hover:text-[#4A6FA5]"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
